/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, Input, Output, EventEmitter, OnInit, OnDestroy
} from '@angular/core';
import { Params } from '@angular/router';
import { Observable, Subscription, combineLatest, iif, map, of, switchMap, tap, zip } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { DatePipe } from '@angular/common';
import { differenceInDays } from 'date-fns';

import {
  HeaderSearchDataInfoText, HeaderWidget, HeaderWidgetConfig, HeaderWidgetType
} from '@design-system/components/templates/header/entities/header.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { DsHeaderConfig, HeaderProductType } from '@header/entities/ds-header-config';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { FlightType } from '@search-bar/entities/flight-type';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { guestTotalNumber, totalRoomsNumber } from '@ux/mappers/guest.mapper';
import { PlaceSelectors } from '@app/entities/place';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { HotelDestinationInputType } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { isEmpty, isNullOrUndefined } from '@app/util/util';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { NavigationService } from '@app/services/navigation.service';
import { getActivityResultCodeType, getActivityResultId } from '@activities/activities-results/mappers/activities-routing.mapper';
import { ActivitySearchCodeType } from '@activities/entities/activities-acutocomplete';

@Component({
  selector: 'app-header-results',
  templateUrl: './header-results.container.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HeaderResultsContainer implements OnInit, OnDestroy {

  @Input() dsHeaderConfig: DsHeaderConfig;
  @Input() parameters: Params;

  @Output() widgetClicked: EventEmitter<HeaderWidget> = new EventEmitter<HeaderWidget>();

  private subscriptions: Subscription = new Subscription();
  private params: Params;

  private readonly dateFormat = 'd MMM';
  private readonly dateFormatWithWeekDay = `E, ${this.dateFormat}`;

  constructor(
    private readonly transloco: TranslocoService,
    private readonly searchBarFacade: BaseSearchBarFacade,
    private readonly datePipe: DatePipe,
    private readonly placeNamePipe: PlaceNamePipe,
    private readonly navigationService: NavigationService,
  ) { }

  ngOnInit(): void {
    if (isNullOrUndefined(this.parameters)) {
      this.initParams();
      this.subscriptions.add(
        this.params$.pipe(
          tap(params => {
            this.params = params;
          })
        ).subscribe()
      );
    } else {
      this.params = this.parameters;
    }
  }

  ngOnDestroy(): void {
    this.searchBarFacade.setEditSearchByProduct(null, null);
    this.subscriptions?.unsubscribe();
  }

  get widgets$(): Observable<HeaderWidgetConfig[]> {
    return combineLatest([
      this.isEditButtonVisible$, this.transloco.selectTranslate('BUTTONS.EDIT'), this.isOneWayTrip$,
      this.flightClass$, this.destinationName$, this.carsPlaceNameByCode$, this.cityName$
    ]).pipe(
      map(([
        isEditButtonVisible, editLabel, isOneWayTrip, flightClass, destinationName, carsPlaceNameByCode, cityName
      ]) => {
        const widgets = [
          {
            Type: HeaderWidgetType.Back,
            Label: null,
          },
          {
            Type: HeaderWidgetType.SearchInfo,
            SearchDataInfo: {
              Titles: [],
              Dates: '',
              Subtitles: []
            }
          }
        ];
        widgets[1].SearchDataInfo.Titles = this.getWidgetTitles(
          isOneWayTrip, destinationName, carsPlaceNameByCode, cityName
        );
        widgets[1].SearchDataInfo.Dates = this.getWidgetDates(isOneWayTrip);
        if (!isEmpty(widgets[1].SearchDataInfo.Dates)) {
          widgets[1].SearchDataInfo.Subtitles = this.getWidgetSubtitles(flightClass);
        }
        if (isEditButtonVisible) {
          const isActivities = this.dsHeaderConfig?.productType === HeaderProductType.Activities;
          if (isActivities || (!isActivities && !isEmpty(widgets[1].SearchDataInfo.Dates))) {
            widgets.push({
              Type: HeaderWidgetType.Edit,
              Label: editLabel
            });
          }
        }
        return widgets;
      })
    );
  }

  private get isEditButtonVisible$(): Observable<boolean> {
    return this.searchBarFacade.isHeaderEditButtonVisible$.pipe(
      map(isHeaderEditButtonVisible => isHeaderEditButtonVisible)
    );
  }

  private get isOneWayTrip$(): Observable<boolean> {
    if (
      this.dsHeaderConfig?.productType === HeaderProductType.Air ||
      this.dsHeaderConfig?.productType === HeaderProductType.Exchanges
    ) {
      return this.searchBarFacade.flightType$.pipe(map(flightType => flightType === FlightType.ONEWAY));
    } else {
      return of(false);
    }
  }

  private get flightClass$(): Observable<FlightClass> {
    if (
      this.dsHeaderConfig?.productType === HeaderProductType.Air
    ) {
      return this.searchBarFacade.flightClass$;
    } else {
      return of(null);
    }
  }

  private get destinationName$(): Observable<string> {
    if (
      this.dsHeaderConfig?.productType === HeaderProductType.Hotels ||
      this.dsHeaderConfig?.productType === HeaderProductType.ByHours ||
      this.dsHeaderConfig?.productType === HeaderProductType.Packages
    ) {
      return iif(
        () => this.dsHeaderConfig?.productType === HeaderProductType.Packages,
        this.searchBarFacade.packageDestinationPlace$,
        this.searchBarFacade.hotelDestinationPlace$
      ).pipe(
        switchMap(destinationPlace => {
          switch (destinationPlace?.InputType) {
            case HotelDestinationInputType.hotel:
              return of(destinationPlace.hotel_name);
            case HotelDestinationInputType.location:
              if (isNullOrUndefined(destinationPlace.NameFull)) {
                return zip(
                  this.placeNamePipe.transform(destinationPlace.NearestIata, PlaceSelectors.Country),
                  this.placeNamePipe.transform(destinationPlace.NearestIata, PlaceSelectors.City)
                ).pipe(
                  map(([country, city]) => `${city}, ${country}`)
                );
              } else {
                return of(destinationPlace.NameFull);
              }
            default:
              return of(null);
          }
        })
      );
    } else {
      return of(null);
    }
  }

  private get carsPlaceNameByCode$(): Observable<string> {
    if (this.dsHeaderConfig?.productType === HeaderProductType.Cars) {
      const pickUpPlace = (this.params?.pick_up_place?.indexOf(',') > -1) ? null :
        (this.params?.pick_up_place?.indexOf('airport:') > -1) ? this.params?.pick_up_place?.replace('airport:', '') :
          this.params?.pick_up_place;
      return this.placeNamePipe.transform(pickUpPlace).pipe(
        switchMap(place => iif(
          () => place !== pickUpPlace,
          of(place), this.searchBarFacade.searchData$
        )),
        map(data => {
          if (typeof data === 'string') {
            return data as string;
          } else {
            const place = (data as SearchData).departurePlace;
            return !isEmpty(place) ? place.country ? `${place.description}, ${place.country}` :
              place.country : pickUpPlace;
          }
        })
      );
    } else {
      return of(null);
    }
  }

  private get cityName$(): Observable<string> {
    if (this.dsHeaderConfig?.productType === HeaderProductType.Activities) {
      const id = getActivityResultId(this.params?.city);
      const codeType = getActivityResultCodeType(this.params?.city);
      return codeType === ActivitySearchCodeType.CityCode ?
        (id ? this.placeNamePipe.transform(id, PlaceSelectors.City) : of(null)) :
        this.searchBarFacade.searchData$.pipe(map(searchData => searchData.inputTravelExtra?.activityName));
    } else {
      return of(null);
    }
  }

  private get params$(): Observable<Params> {
    return this.navigationService.events$.pipe(
      map(({ rootRoute }) => rootRoute?.snapshot?.paramMap?.['params']),
    );
  }

  private getWidgetDates(isOneWayTrip: boolean): string {
    let leftDate, rightDate = null;
    switch (this.dsHeaderConfig?.productType) {
      case HeaderProductType.Air:
      case HeaderProductType.Exchanges:
      case HeaderProductType.Packages:
        const format = this.dsHeaderConfig?.productType === HeaderProductType.Packages ?
          this.dateFormatWithWeekDay : this.dateFormat;
        leftDate = this.params?.departure_date ? this.datePipe.transform(
          formatDateByRegex(this.params?.departure_date), format
        ) : '';
        rightDate = this.params?.arrival_date ? isOneWayTrip ? null : this.datePipe.transform(
          formatDateByRegex(this.params?.arrival_date), format
        ) : '';
        return rightDate && leftDate ? `${leftDate} - ${rightDate}` : leftDate;
      case HeaderProductType.Hotels:
        leftDate = this.params?.check_in ? this.datePipe.transform(
          formatDateByRegex(this.params?.check_in), this.dateFormatWithWeekDay
        ) : '';
        rightDate = this.params?.check_out ? this.datePipe.transform(
          formatDateByRegex(this.params?.check_out), this.dateFormatWithWeekDay
        ) : '';
        return `${leftDate} - ${rightDate}`;
      case HeaderProductType.ByHours:
        return this.params?.check_in ? this.datePipe.transform(
          formatDateByRegex(this.params?.check_in), this.dateFormatWithWeekDay
        ) : '';
      case HeaderProductType.Cars:
        leftDate = this.params?.pick_up_date ? this.datePipe.transform(
          formatDateByRegex(this.params?.pick_up_date.replace(/-/g, '/').split('T')[0]), this.dateFormatWithWeekDay
        ) : '';
        rightDate = this.params?.drop_off_date ? this.datePipe.transform(
          formatDateByRegex(this.params?.drop_off_date.replace(/-/g, '/').split('T')[0]), this.dateFormatWithWeekDay
        ) : '';
        return leftDate && rightDate ? `${leftDate} - ${rightDate}` : '';
      default:
        return '';
    }
  }

  private getWidgetTitles(
    isOneWayTrip: boolean, destinationName: string, carsPlaceNameByCode: string, cityName: string
  ): HeaderSearchDataInfoText[] {
    switch (this.dsHeaderConfig?.productType) {
      case HeaderProductType.Air:
      case HeaderProductType.Exchanges:
        return [
          this.params?.departure && {
            Text: this.params?.departure,
          },
          this.params?.arrival && {
            Text: this.params?.arrival,
            Icon: isOneWayTrip ? Icon.ArrowRight : Icon.Roundtrip
          }
        ];
      case HeaderProductType.Hotels:
      case HeaderProductType.ByHours:
      case HeaderProductType.Packages:
        return [
          {
            Text: destinationName,
          },
        ];
      case HeaderProductType.Cars:
        return [
          {
            Text: carsPlaceNameByCode ?? '',
          },
        ];
      case HeaderProductType.Activities:
        return [
          {
            Text: cityName,
          },
        ];
      default:
        return [];
    }
  }

  private getWidgetSubtitles(flightClass: FlightClass): HeaderSearchDataInfoText[] {
    switch (this.dsHeaderConfig?.productType) {
      case HeaderProductType.Air:
      case HeaderProductType.Exchanges:
        const adultsQuantity = parseInt(this.params?.adults);
        const childrenAndInfantsQuantity = parseInt(this.params?.children) + parseInt(this.params?.infants);
        const passengersQuantity = !isNaN(adultsQuantity) ? !isNaN(childrenAndInfantsQuantity) ?
          adultsQuantity + childrenAndInfantsQuantity : adultsQuantity : 0;
        return [
          passengersQuantity && {
            Icon: Icon.PersonOutline,
            Text: `${passengersQuantity}`
          },
          flightClass && {
            Text: this.transloco.translate(`SEARCH_MODULE.FLIGHT_CLASSES.${flightClass}`)
          }
        ];
      case HeaderProductType.Hotels:
      case HeaderProductType.Packages:
        return [
          this.params?.rooms && {
            Icon: Icon.Bed,
            Text: `${totalRoomsNumber(this.params)}`
          },
          this.params?.rooms && {
            Icon: Icon.PersonOutline,
            Text: `${guestTotalNumber(this.params)}`
          },
        ];
      case HeaderProductType.ByHours:
        return [];
      case HeaderProductType.Cars:
        const pickUp = this.params?.pick_up_date ?? null;
        const dropOff = this.params?.drop_off_date ?? null;
        const distance = dropOff && pickUp ? Math.max(1, differenceInDays(new Date(dropOff), new Date(pickUp))) : 0;
        const days = distance > 1 ? 'AUTOS_MODULE.CARD.PRICE_DAYS' : 'AUTOS_MODULE.CARD.PRICE_DAY';
        return [
          distance && {
            Icon: Icon.Calendar,
            Text: `${distance} ${this.transloco.translate(days)}`
          },
        ];
      default:
        return [];
    }
  }

  private initParams(): void {
    const root = this.navigationService.rootRoute;
    if (root.outlet === 'primary') {
      this.params = root.snapshot.paramMap?.['params'];
    }
  }

}
